import { activities as activitiesData, projects } from "../../../../../db";
import {
  calculateDurationInMonths,
  calculateProjectActivityIntersection,
  calculateProfessionalAttributedProjectDuration
} from '../calculators/skillCalculationUtils';
import { SKILL_QUERY_KEYS, queryClient } from '../../queryClient';

interface ProjectDetail {
  name: string;
  startDate: string;
  endDate: string | null;
  duration: number;
  weight: number;
  fullDuration?: number; // For showing full project duration vs intersection
  isIntersection?: boolean; // Flag to indicate if duration is intersection
}

interface LiveCalculationData {
  skillName: string;
  totalProjects: number;
  projectsWithSkill: number;
  totalDuration: number;
  projects: ProjectDetail[];
  totalSkillPoints: number;
  skillSpecificPoints: number;
  finalResult: number;
  activityPeriod?: {
    start: string;
    end: string | null;
  };
  allProjectDurations?: Array<{
    name: string;
    duration: number;
  }>;
}

// Helper function to find a skill in a project across all categories
const findSkillInProject = (project: any, skillName: string): { weight: number, category: string } | null => {
  // Check categorizedSkills first
  if (project.categorizedSkills) {
    const categories = ['profession', 'lead', 'soft', 'language', 'tools'];
    for (const category of categories) {
      const categorySkills = project.categorizedSkills[category];
      if (categorySkills) {
        const skill = categorySkills.find((s: any) => s.name === skillName);
        if (skill) return { weight: skill.weight || 0, category };
      }
    }
  }
  
  // Check legacy format
  if (project.skills) {
    const skill = project.skills.find((s: any) => s.name === skillName);
    if (skill) return { weight: skill.weight || 0, category: 'profession' };
  }
  
  if (project.tech) {
    const skill = project.tech.find((s: any) => s.name === skillName);
    if (skill) return { weight: skill.weight || 0, category: 'tools' };
  }

  // For projects with GitHub repos, try to get actual GitHub language data from the project's enhanced data
  if (project.git && project.git.includes('github.com') && 
      !project.git.includes('not') && !project.git.includes('inner source') && !project.git.includes('private')) {
    
    // Check if the project has GitHub-enhanced language data
    if (project.githubLanguages && Array.isArray(project.githubLanguages)) {
      const githubLanguage = project.githubLanguages.find((lang: any) => lang.name === skillName);
      if (githubLanguage && typeof githubLanguage.weight === 'number') {
        return { weight: githubLanguage.weight, category: 'language' };
      }
    }
  }
  
  return null;
};

// Helper function to get first skill from any category
const getFirstSkillFromCategories = (skillsByCategory: any): { skill: any, category: string } | null => {
  const categories = ['profession', 'lead', 'soft', 'language', 'tools'];
  
  for (const category of categories) {
    const skills = skillsByCategory[category] || [];
    if (skills.length > 0) {
      return { skill: skills[0], category };
    }
  }
  
  return null;
};

// Generate live calculation example for professional skills
export const generateProfessionalSkillExample = (skillsByCategory: any): LiveCalculationData | null => {
  const firstSkillData = getFirstSkillFromCategories(skillsByCategory);
  if (!firstSkillData) return null;
  
  const { skill: firstSkill, category } = firstSkillData;
  
  // Find all professional projects (excluding private)
  const allProfessionalProjects = projects.filter((project: any) => project.activityId !== null);
  
  // Find projects with this skill (excluding private projects)
  const relevantProjects = projects.filter((project: any) => {
    if (project.activityId === null) return false;
    return findSkillInProject(project, firstSkill.name) !== null;
  });
  
  // Calculate total professional duration (total skill points) - handle multi-activity projects
  const totalProfessionalMonths = allProfessionalProjects.reduce((total, project: any) => {
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    return total + attributedDuration;
  }, 0);
  
  // Calculate weighted months for this skill and build project details
  let totalWeightedMonths = 0;
  const projectDetails: ProjectDetail[] = [];
  
  // First calculate total weighted months for ALL relevant projects (for percentage calculation)
  let fullTotalWeightedMonths = 0;
  relevantProjects.forEach((project: any) => {
    const skillData = findSkillInProject(project, firstSkill.name);
    const skillWeight = skillData?.weight || 0;
    
    // Handle multi-activity projects
    let attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    
    fullTotalWeightedMonths += attributedDuration * skillWeight;
  });
  
  // Sort relevant projects by start date (most recent first) to show most recent projects
  relevantProjects.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  relevantProjects.forEach((project: any) => { // Show all relevant projects that have the skill
    const skillData = findSkillInProject(project, firstSkill.name);
    const skillWeight = skillData?.weight || 0;
    
    // Handle multi-activity projects
    let attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    
    const contribution = attributedDuration * skillWeight;
    totalWeightedMonths += contribution;
    
    projectDetails.push({
      name: project.title,
      startDate: project.startDate,
      endDate: project.endDate,
      duration: attributedDuration,
      weight: skillWeight
    });
  });
  
  const percentage = totalProfessionalMonths > 0 ? (fullTotalWeightedMonths / totalProfessionalMonths) * 100 : 0;
  
  // Collect all project durations for display
  const allProjectDurations = allProfessionalProjects.map((project: any) => {
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    return {
      name: project.title,
      duration: attributedDuration
    };
  });
  
  return {
    skillName: firstSkill.name,
    totalProjects: allProfessionalProjects.length,
    projectsWithSkill: relevantProjects.length,
    totalDuration: totalProfessionalMonths,
    projects: projectDetails,
    totalSkillPoints: totalProfessionalMonths,
    skillSpecificPoints: fullTotalWeightedMonths, // Use full calculation, not just displayed projects
    finalResult: Math.round(percentage),
    allProjectDurations
  };
};

// Generate live calculation example for activity skills
export const generateActivitySkillExample = (skillsByCategory: any, activityId: number): LiveCalculationData | null => {
  const firstSkillData = getFirstSkillFromCategories(skillsByCategory);
  if (!firstSkillData) return null;
  
  const { skill: firstSkill, category } = firstSkillData;
  const activity = activitiesData.find(a => a.id === activityId);
  if (!activity) return null;
  
  // Find all activity projects
  const allActivityProjects = projects.filter((project: any) => {
    if (project.activityId === null) return false;
    
    if (Array.isArray(project.activityId)) {
      return project.activityId.includes(activityId);
    }
    return project.activityId === activityId;
  });
  
  // Find activity projects with this skill
  const relevantProjects = allActivityProjects.filter((project: any) => {
    return findSkillInProject(project, firstSkill.name) !== null;
  });
  
  if (relevantProjects.length === 0) return null;
  
  // Calculate total activity project duration (using intersection for continued projects)
  const totalActivityProjectMonths = allActivityProjects.reduce((total, project: any) => {
    return total + calculateProjectActivityIntersection(project, activity);
  }, 0);
  
  // Calculate activity duration
  const activityDuration = calculateDurationInMonths(activity.startDate, activity.endDate);
  
  // Calculate weighted months for this skill in this activity
  // Use ALL relevant projects, not just the first 3 for display
  let totalWeightedMonths = 0;
  const projectDetails: ProjectDetail[] = [];
  
  // Process ALL relevant projects for accurate calculation
  relevantProjects.forEach((project: any) => {
    // Calculate intersection duration (critical for continued projects)
    const intersectionDuration = calculateProjectActivityIntersection(project, activity);
    const skillData = findSkillInProject(project, firstSkill.name);
    const skillWeight = skillData?.weight || 0;
    
    const contribution = intersectionDuration * skillWeight;
    totalWeightedMonths += contribution;
  });

  // Sort relevant projects by start date (most recent first) to show most recent projects
  relevantProjects.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  // Show all relevant projects that have the skill
  relevantProjects.forEach((project: any) => {
    const intersectionDuration = calculateProjectActivityIntersection(project, activity);
    const fullProjectDuration = calculateDurationInMonths(project.startDate, project.endDate);
    const skillData = findSkillInProject(project, firstSkill.name);
    const skillWeight = skillData?.weight || 0;
    
    projectDetails.push({
      name: project.title,
      startDate: project.startDate,
      endDate: project.endDate,
      duration: intersectionDuration, // Use intersection, not full duration
      weight: skillWeight,
      fullDuration: fullProjectDuration, // Keep full duration for display
      isIntersection: intersectionDuration !== fullProjectDuration // Flag for display
    });
  });
  
  const percentage = totalActivityProjectMonths > 0 ? (totalWeightedMonths / totalActivityProjectMonths) * 100 : 0;
  
  // Collect all activity project durations for display
  const allProjectDurations = allActivityProjects.map((project: any) => {
    const intersectionDuration = calculateProjectActivityIntersection(project, activity);
    return {
      name: project.title,
      duration: intersectionDuration
    };
  });
  
  return {
    skillName: firstSkill.name,
    totalProjects: allActivityProjects.length,
    projectsWithSkill: relevantProjects.length,
    totalDuration: activityDuration,
    projects: projectDetails,
    totalSkillPoints: totalActivityProjectMonths,
    skillSpecificPoints: totalWeightedMonths,
    finalResult: Math.round(percentage),
    activityPeriod: {
      start: activity.startDate,
      end: activity.endDate
    },
    allProjectDurations
  };
};

// Generate live calculation example for company skills
export const generateCompanySkillExample = (skillsByCategory: any, activities: any[]): LiveCalculationData | null => {
  const firstSkillData = getFirstSkillFromCategories(skillsByCategory);
  if (!firstSkillData) return null;
  
  const { skill: firstSkill, category } = firstSkillData;
  
  // Find company projects with this skill
  const activityIds = activities.map(a => a.id);
  
  // Find all company projects
  const allCompanyProjects = projects.filter((project: any) => {
    if (project.activityId === null) return false;
    
    if (Array.isArray(project.activityId)) {
      return project.activityId.some((id: number) => activityIds.includes(id));
    }
    return activityIds.includes(project.activityId);
  });
  
  // Find projects with this skill
  const relevantProjects = allCompanyProjects.filter((project: any) => {
    return findSkillInProject(project, firstSkill.name) !== null;
  });
  
  if (relevantProjects.length === 0) return null;
  
  // Calculate total company duration and total attributed project duration
  const totalCompanyDuration = activities.reduce((total, activity) => {
    return total + calculateDurationInMonths(activity.startDate, activity.endDate);
  }, 0);
  
  // First, calculate total attributed project duration for ALL projects at this company
  let totalAttributedProjectDuration = 0;
  allCompanyProjects.forEach((project: any) => {
    const projectDuration = calculateDurationInMonths(project.startDate, project.endDate);
    
    // Calculate attributed duration for multi-activity projects
    let attributedDuration = projectDuration;
    if (Array.isArray(project.activityId)) {
      const activitiesAtCompany = project.activityId.filter((id: number) => activityIds.includes(id)).length;
      const totalActivities = project.activityId.length;
      
      // If ALL activities of this project are at this company, count full duration
      // Otherwise, count proportionally
      if (activitiesAtCompany === totalActivities) {
        attributedDuration = projectDuration;
      } else {
        attributedDuration = (projectDuration * activitiesAtCompany) / totalActivities;
      }
    }
    
    totalAttributedProjectDuration += attributedDuration;
  });
  
  // Now calculate weighted months for this skill and build project details
  let totalWeightedMonths = 0;
  const projectDetails: ProjectDetail[] = [];
  
  // Sort relevant projects by start date (most recent first) to show most recent projects
  relevantProjects.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  relevantProjects.forEach((project: any) => { // Show all relevant projects that have the skill
    const projectDuration = calculateDurationInMonths(project.startDate, project.endDate);
    
    const skillData = findSkillInProject(project, firstSkill.name);
    const skillWeight = skillData?.weight || 0;
    
    // Calculate attributed duration for multi-activity projects
    let attributedDuration = projectDuration;
    if (Array.isArray(project.activityId)) {
      const activitiesAtCompany = project.activityId.filter((id: number) => activityIds.includes(id)).length;
      const totalActivities = project.activityId.length;
      
      // If ALL activities of this project are at this company, count full duration
      // Otherwise, count proportionally
      if (activitiesAtCompany === totalActivities) {
        attributedDuration = projectDuration;
      } else {
        attributedDuration = (projectDuration * activitiesAtCompany) / totalActivities;
      }
    }
    
    const contribution = attributedDuration * skillWeight;
    totalWeightedMonths += contribution;
    
    projectDetails.push({
      name: project.title,
      startDate: project.startDate,
      endDate: project.endDate,
      duration: attributedDuration,
      weight: skillWeight
    });
  });
  
  // Calculate full weighted months for ALL relevant projects (not just displayed ones)
  let fullTotalWeightedMonths = 0;
  relevantProjects.forEach((project: any) => {
    const projectDuration = calculateDurationInMonths(project.startDate, project.endDate);
    
    const skillData = findSkillInProject(project, firstSkill.name);
    const skillWeight = skillData?.weight || 0;
    
    // Calculate attributed duration for multi-activity projects
    let attributedDuration = projectDuration;
    if (Array.isArray(project.activityId)) {
      const activitiesAtCompany = project.activityId.filter((id: number) => activityIds.includes(id)).length;
      const totalActivities = project.activityId.length;
      
      // If ALL activities of this project are at this company, count full duration
      // Otherwise, count proportionally
      if (activitiesAtCompany === totalActivities) {
        attributedDuration = projectDuration;
      } else {
        attributedDuration = (projectDuration * activitiesAtCompany) / totalActivities;
      }
    }
    
    const contribution = attributedDuration * skillWeight;
    fullTotalWeightedMonths += contribution;
  });
  
  const percentage = totalAttributedProjectDuration > 0 ? (fullTotalWeightedMonths / totalAttributedProjectDuration) * 100 : 0;
  
  // Collect all company project durations for display
  const allProjectDurations = allCompanyProjects.map((project: any) => {
    const projectDuration = calculateDurationInMonths(project.startDate, project.endDate);
    
    // Calculate attributed duration for multi-activity projects
    let attributedDuration = projectDuration;
    if (Array.isArray(project.activityId)) {
      const activitiesAtCompany = project.activityId.filter((id: number) => activityIds.includes(id)).length;
      const totalActivities = project.activityId.length;
      
      // If ALL activities of this project are at this company, count full duration
      // Otherwise, count proportionally
      if (activitiesAtCompany === totalActivities) {
        attributedDuration = projectDuration;
      } else {
        attributedDuration = (projectDuration * activitiesAtCompany) / totalActivities;
      }
    }
    
    return {
      name: project.title,
      duration: attributedDuration
    };
  });
  
  return {
    skillName: firstSkill.name,
    totalProjects: allCompanyProjects.length,
    projectsWithSkill: relevantProjects.length,
    totalDuration: totalCompanyDuration,
    projects: projectDetails,
    totalSkillPoints: totalAttributedProjectDuration, // Total attributed project duration (can exceed company duration)
    skillSpecificPoints: fullTotalWeightedMonths, // Use full calculation for ALL projects, not just displayed ones
    finalResult: Math.round(percentage),
    allProjectDurations
  };
};

// Generate live calculation example for private skills
export const generatePrivateSkillExample = (skillsByCategory: any): LiveCalculationData | null => {
  const firstSkillData = getFirstSkillFromCategories(skillsByCategory);
  if (!firstSkillData) return null;
  
  const { skill: firstSkill, category } = firstSkillData;
  
  // Find all private projects
  const allPrivateProjects = projects.filter((project: any) => project.activityId === null);
  
  let relevantProjects: any[] = [];
  let totalPrivateMonths = 0;
  let fullTotalWeightedMonths = 0;
  
  // Calculate total private duration
  totalPrivateMonths = allPrivateProjects.reduce((total, project: any) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    return total + duration;
  }, 0);
  
  // For language skills, we need to handle GitHub-enhanced skills differently
  if (category === 'language') {
    // Find projects that have this language skill (either static or GitHub-enhanced)
    allPrivateProjects.forEach((project: any) => {
      let hasSkill = false;
      let skillWeight = 0;
      
      // For language skills, always use the weight from the skillsByCategory data
      // because it reflects the actual calculated weight (including GitHub enhancement)
      skillWeight = firstSkill.level / 100; // firstSkill.level is the percentage (e.g., 72)
      
      // Check if this project should have this skill
      // First check static categorized skills
      if (project.categorizedSkills?.language) {
        const staticSkill = project.categorizedSkills.language.find((s: any) => s.name === firstSkill.name);
        if (staticSkill) {
          hasSkill = true;
        }
      }
      
      // If not found in static skills and project has GitHub repo, this is a GitHub-enhanced skill
      if (!hasSkill && project.git && project.git.includes('github.com') && 
          !project.git.includes('not') && !project.git.includes('inner source') && !project.git.includes('private')) {
        hasSkill = true;
      }
      
      if (hasSkill) {
        const duration = calculateDurationInMonths(project.startDate, project.endDate);
        const weightedMonths = duration * skillWeight;
        fullTotalWeightedMonths += weightedMonths;
        
        relevantProjects.push({
          name: project.title,
          startDate: project.startDate,
          endDate: project.endDate,
          duration: duration,
          weight: skillWeight
        });
      }
    });
  } else {
    // For non-language skills, use the original logic
    relevantProjects = projects.filter((project: any) => {
      if (project.activityId !== null) return false;
      return findSkillInProject(project, firstSkill.name) !== null;
    });
    
    relevantProjects.forEach((project: any) => {
      const skillData = findSkillInProject(project, firstSkill.name);
      if (!skillData) return;

      const duration = calculateDurationInMonths(project.startDate, project.endDate);
      const skillWeight = skillData.weight;
      const weightedMonths = duration * skillWeight;
      
      fullTotalWeightedMonths += weightedMonths;
    });
    
    // Convert to the format expected by the UI
    relevantProjects = relevantProjects.map((project: any) => {
      const skillData = findSkillInProject(project, firstSkill.name);
      const duration = calculateDurationInMonths(project.startDate, project.endDate);
      return {
        name: project.title,
        startDate: project.startDate,
        endDate: project.endDate,
        duration: duration,
        weight: skillData?.weight || 0
      };
    });
  }
  
  // Sort relevant projects by start date (most recent first) to show most recent projects
  relevantProjects.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  const percentage = totalPrivateMonths > 0 ? (fullTotalWeightedMonths / totalPrivateMonths) * 100 : 0;
  
  // Collect all private project durations for display
  const allProjectDurations = allPrivateProjects.map((project: any) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    return {
      name: project.title,
      duration: duration
    };
  });
  
  return {
    skillName: firstSkill.name,
    totalProjects: allPrivateProjects.length,
    projectsWithSkill: relevantProjects.length,
    totalDuration: totalPrivateMonths,
    projects: relevantProjects,
    totalSkillPoints: totalPrivateMonths,
    skillSpecificPoints: fullTotalWeightedMonths,
    finalResult: Math.round(percentage),
    allProjectDurations
  };
};

// Generate live calculation example for project-specific skills
export const generateProjectSkillExample = (skillsByCategory: any, projectTitle: string): LiveCalculationData | null => {
  const firstSkillData = getFirstSkillFromCategories(skillsByCategory);
  if (!firstSkillData) return null;
  
  const { skill: firstSkill, category } = firstSkillData;
  
  // Find the specific project
  const targetProject = projects.find((project: any) => project.title === projectTitle);
  if (!targetProject) return null;
  
  // Check if this project has the skill
  const skillData = findSkillInProject(targetProject, firstSkill.name);
  if (!skillData) return null;
  
  // Calculate project duration
  const projectDuration = calculateDurationInMonths(targetProject.startDate, targetProject.endDate);
  
  // Calculate skill-specific points for this project
  const skillSpecificPoints = projectDuration * skillData.weight;
  
  // For project calculation, we show this project's contribution vs the total project duration
  // This gives a meaningful ratio: weighted_time_on_skill / total_project_time
  const percentage = projectDuration > 0 ? (skillSpecificPoints / projectDuration) * 100 : 0;
  
  // Create project detail for this single project
  const projectDetails: ProjectDetail[] = [{
    name: targetProject.title,
    startDate: targetProject.startDate,
    endDate: targetProject.endDate,
    duration: projectDuration,
    weight: skillData.weight
  }];
  
  return {
    skillName: firstSkill.name,
    totalProjects: 1, // Just this project
    projectsWithSkill: 1,
    totalDuration: projectDuration,
    projects: projectDetails,
    totalSkillPoints: projectDuration, // Total project duration
    skillSpecificPoints: skillSpecificPoints, // Weighted skill time
    finalResult: Math.round(percentage),
    allProjectDurations: [{
      name: targetProject.title,
      duration: projectDuration
    }]
  };
};

/**
 * Get GitHub language data for a project using direct API call when needed
 */
async function getGitHubLanguageData(projectGitUrl: string, skillName: string): Promise<number | null> {
  try {
    // Try to get actual GitHub language data
    const { fetchGitHubLanguages } = await import('../github/githubLanguages');
    const languages = await fetchGitHubLanguages(projectGitUrl);
    
    if (languages && languages.length > 0) {
      const language = languages.find((lang: any) => lang.name === skillName);
      if (language && typeof language.weight === 'number') {
        return language.weight;
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to fetch GitHub language data:', error);
    return null;
  }
}

// Generate live calculation example for combined professional and private skills
export const generateCombinedSkillExample = (skillsByCategory: any, enhancedProjects?: any[]): LiveCalculationData | null => {
  const firstSkillData = getFirstSkillFromCategories(skillsByCategory);
  if (!firstSkillData) return null;
  
  const { skill: firstSkill, category } = firstSkillData;
  
  // Use enhanced projects if available, otherwise fall back to static projects
  const allProjects = enhancedProjects || projects;
  
  // Calculate total duration across ALL projects
  const totalCombinedMonths = allProjects.reduce((total, project: any) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    return total + duration;
  }, 0);
  
  // Find projects with this skill - comprehensive approach for all skill types
  let relevantProjects: any[] = [];
  let fullTotalWeightedMonths = 0;
  
  // Check ALL projects for this skill
  allProjects.forEach((project: any) => {
    let hasSkill = false;
    let skillWeight = 0;
    
    // First check if this project has the skill statically defined
    const staticSkillData = findSkillInProject(project, firstSkill.name);
    if (staticSkillData) {
      hasSkill = true;
      skillWeight = staticSkillData.weight;
    }
    
    // For language skills ONLY, also check for GitHub-enhanced detection
    if (!hasSkill && category === 'language' && project.git && project.git.includes('github.com') && 
        !project.git.includes('not') && !project.git.includes('inner source') && !project.git.includes('private')) {
      
      // Check if the project has GitHub-enhanced language data  
      if (project.githubLanguages && Array.isArray(project.githubLanguages)) {
        const githubLanguage = project.githubLanguages.find((lang: any) => lang.name === firstSkill.name);
        if (githubLanguage && typeof githubLanguage.weight === 'number') {
          hasSkill = true;
          skillWeight = githubLanguage.weight;
        }
      }
    }
    
    if (hasSkill) {
      const duration = calculateDurationInMonths(project.startDate, project.endDate);
      fullTotalWeightedMonths += duration * skillWeight;
      
      relevantProjects.push({
        name: project.title,
        startDate: project.startDate,
        endDate: project.endDate,
        duration: duration,
        weight: skillWeight
      });
    }
  });
  
  if (relevantProjects.length === 0) return null;
  
  // Sort relevant projects by start date (most recent first) to ensure ChatMate and other recent projects are shown
  relevantProjects.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  // Calculate percentage
  const percentage = totalCombinedMonths > 0 ? (fullTotalWeightedMonths / totalCombinedMonths) * 100 : 0;
  
  // Show all relevant projects that have the skill (no artificial limit)
  // This ensures all skill contributions are visible in the calculation breakdown
  const projectDetails = relevantProjects;
  
  // Collect all project durations for display
  const allProjectDurations = allProjects.map((project: any) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    return {
      name: project.title,
      duration: duration
    };
  });
  
  return {
    skillName: firstSkill.name,
    totalProjects: allProjects.length,
    projectsWithSkill: relevantProjects.length,
    totalDuration: totalCombinedMonths,
    projects: projectDetails,
    totalSkillPoints: totalCombinedMonths,
    skillSpecificPoints: fullTotalWeightedMonths,
    finalResult: Math.round(percentage),
    allProjectDurations
  };
};
export const generateSpecificSkillCalculation = (
  skillName: string,
  category: 'profession' | 'lead' | 'soft' | 'language' | 'tools',
  calculationType: 'professional' | 'activity' | 'company' | 'project' | 'private' | 'combined',
  skillsByCategory: any,
  activityId?: number,
  activities?: any[],
  projectTitle?: string,
  enhancedProjects?: any[]
): LiveCalculationData | null => {
  // Find the skill in the specified category first
  let categorySkills = skillsByCategory[category] || [];
  let targetSkill = categorySkills.find((skill: any) => skill.name === skillName);
  let actualCategory = category;
  
  // If not found in the specified category, search all categories
  if (!targetSkill) {
    const allCategories = ['profession', 'lead', 'soft', 'language', 'tools'];
    
    for (const cat of allCategories) {
      const catSkills = skillsByCategory[cat] || [];
      const foundSkill = catSkills.find((skill: any) => skill.name === skillName);
      if (foundSkill) {
        targetSkill = foundSkill;
        actualCategory = cat as typeof category;
        break;
      }
    }
  }
  
  if (!targetSkill) {
    return null;
  }
  
  // Create a temporary skillsByCategory object with just this skill in the correct category
  const tempSkillsByCategory = {
    profession: actualCategory === 'profession' ? [targetSkill] : [],
    lead: actualCategory === 'lead' ? [targetSkill] : [],
    soft: actualCategory === 'soft' ? [targetSkill] : [],
    language: actualCategory === 'language' ? [targetSkill] : [],
    tools: actualCategory === 'tools' ? [targetSkill] : []
  };
  
  // Use the existing generation functions
  switch (calculationType) {
    case 'professional':
      return generateProfessionalSkillExample(tempSkillsByCategory);
    case 'activity':
      return activityId ? generateActivitySkillExample(tempSkillsByCategory, activityId) : null;
    case 'company':
      return activities ? generateCompanySkillExample(tempSkillsByCategory, activities) : null;
    case 'project':
      // For project-level calculations, show project-specific calculation
      return projectTitle ? generateProjectSkillExample(tempSkillsByCategory, projectTitle) : null;
    case 'private':
      return generatePrivateSkillExample(tempSkillsByCategory);
    case 'combined':
      return generateCombinedSkillExample(tempSkillsByCategory, enhancedProjects);
    default:
      return null;
  }
};
